import { useState, useEffect } from 'react';
import { 
  Search, 
  Edit2, 
  CheckCircle, 
  AlertCircle, 
  Shield,
  Eye,
  EyeOff,
  Save,
  Loader2,
  RefreshCw,
  Building2,
  Plus,
  Trash2,
  Database,
  Upload,
  AlertTriangle,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { platforms as staticPlatforms } from '@/data/platforms';
import { platformsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const verificationLevels = [
  { value: 'initial', label: 'Initial', icon: AlertCircle, className: 'text-muted-foreground' },
  { value: 'vendor-verified', label: 'Vendor Verified', icon: CheckCircle, className: 'text-primary' },
  { value: 'independently-reviewed', label: 'Independently Reviewed', icon: Shield, className: 'text-success' },
];

const defaultFeatures = {
  conversational: false,
  codeGeneration: false,
  imageGeneration: false,
  imageAnalysis: false,
  documentAnalysis: false,
  webBrowsing: false,
  apiAccess: false,
  customInstructions: false,
  pluginsExtensions: false,
  voiceInput: false,
  mobileApp: false,
  teamCollaboration: false,
};

export const PlatformManager = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc'); // 'name-asc', 'name-desc', 'provider-asc', 'provider-desc', 'status'
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [dbCount, setDbCount] = useState({ total: 0, enabled: 0 });
  const { toast } = useToast();

  const fetchPlatforms = async () => {
    setLoading(true);
    try {
      const data = await platformsApi.getAll();
      setPlatforms(data);
      const count = await platformsApi.getCount();
      setDbCount(count);
    } catch (err) {
      console.error('Failed to fetch platforms:', err);
      // Fall back to static data if DB is empty
      if (platforms.length === 0) {
        setPlatforms(staticPlatforms.map(p => ({
          ...p,
          verification_status: 'initial',
          is_enabled: true,
        })));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const result = await platformsApi.seed(staticPlatforms);
      toast({
        title: 'Database seeded successfully',
        description: `${result.inserted} new platforms added, ${result.updated} updated.`,
      });
      await fetchPlatforms();
    } catch (err) {
      console.error('Failed to seed database:', err);
      toast({
        title: 'Seeding failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSeeding(false);
    }
  };

  const sortPlatforms = (platformList) => {
    return [...platformList].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'provider-asc':
          return (a.provider || '').localeCompare(b.provider || '');
        case 'provider-desc':
          return (b.provider || '').localeCompare(a.provider || '');
        case 'status':
          const statusOrder = { 'independently-reviewed': 0, 'vendor-verified': 1, 'initial': 2 };
          return (statusOrder[a.verification_status] || 3) - (statusOrder[b.verification_status] || 3);
        default:
          return 0;
      }
    });
  };

  const filteredPlatforms = platforms.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.provider?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (platform) => {
    setEditForm({
      name: platform.name || '',
      provider: platform.provider || '',
      summary: platform.summary || '',
      description: platform.description || '',
      verification_status: platform.verification_status || 'initial',
      is_enabled: platform.is_enabled !== false,
      admin_notes: platform.admin_notes || '',
      sourceTag: platform.sourceTag || '',
      costBand: platform.costBand || 2,
      useCases: platform.useCases || [],
      userTypes: platform.userTypes || [],
      dataSensitivity: platform.dataSensitivity || [],
      technicalLevel: platform.technicalLevel || [],
      pricingModel: platform.pricingModel || [],
      tags: platform.tags || [],
      pros: platform.pros || [],
      cons: platform.cons || [],
      bestFor: platform.bestFor || [],
      notIdealFor: platform.notIdealFor || [],
      features: platform.features || defaultFeatures,
      links: platform.links || {},
    });
    setEditingPlatform(platform);
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setEditForm({
      id: '',
      name: '',
      provider: '',
      summary: '',
      description: '',
      verification_status: 'initial',
      is_enabled: true,
      admin_notes: '',
      sourceTag: '',
      costBand: 2,
      useCases: [],
      userTypes: [],
      dataSensitivity: [],
      technicalLevel: [],
      pricingModel: [],
      tags: [],
      pros: [],
      cons: [],
      bestFor: [],
      notIdealFor: [],
      features: defaultFeatures,
      links: { website: '', documentation: '', pricing: '' },
    });
    setEditingPlatform({ id: 'new' });
    setIsAddingNew(true);
  };

  const handleSave = async () => {
    if (!editingPlatform) return;
    
    setSaving(true);
    try {
      if (isAddingNew) {
        if (!editForm.id || !editForm.name) {
          toast({
            title: 'Validation Error',
            description: 'Platform ID and name are required.',
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }
        await platformsApi.create(editForm);
        toast({
          title: 'Platform created',
          description: `${editForm.name} has been added.`,
        });
      } else {
        await platformsApi.update(editingPlatform.id, editForm);
        toast({
          title: 'Platform updated',
          description: `${editForm.name} has been updated.`,
        });
      }
      await fetchPlatforms();
      setEditingPlatform(null);
    } catch (err) {
      console.error('Failed to save:', err);
      toast({
        title: 'Save failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await platformsApi.delete(deleteConfirm.id);
      toast({
        title: 'Platform deleted',
        description: `${deleteConfirm.name} has been removed.`,
      });
      await fetchPlatforms();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast({
        title: 'Delete failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getVerificationBadge = (platform) => {
    const status = platform?.verification_status || 'initial';
    const config = verificationLevels.find(v => v.value === status) || verificationLevels[0];
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={cn('text-xs gap-1', config.className)}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Group platforms by source and apply sorting
  const techExPlatforms = sortPlatforms(filteredPlatforms.filter(p => p.sourceTag === 'TechEx Global 2026'));
  const otherPlatforms = sortPlatforms(filteredPlatforms.filter(p => p.sourceTag !== 'TechEx Global 2026'));
  const needsSeeding = dbCount.total === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Platform Management</h2>
          <p className="text-sm text-muted-foreground">
            Add, edit, and manage AI platform data
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchPlatforms} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleAddNew}>
            <Plus className="w-4 h-4 mr-2" />
            Add Platform
          </Button>
        </div>
      </div>

      {/* Database Status / Seed Card */}
      {needsSeeding ? (
        <Card className="border-warning/50 bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Database Migration Required
            </CardTitle>
            <CardDescription>
              Platform data is currently stored in a static file. Click below to migrate all {staticPlatforms.length} platforms to the database for dynamic management.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSeedDatabase} disabled={seeding} className="gap-2">
              {seeding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {seeding ? 'Migrating...' : `Migrate ${staticPlatforms.length} Platforms to Database`}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">{dbCount.total} platforms in database</p>
                  <p className="text-xs text-muted-foreground">{dbCount.enabled} enabled</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleSeedDatabase} disabled={seeding}>
                {seeding ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sync from Static Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search platforms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">
              <span className="flex items-center gap-2">
                <ArrowUp className="w-3 h-3" /> Name (A-Z)
              </span>
            </SelectItem>
            <SelectItem value="name-desc">
              <span className="flex items-center gap-2">
                <ArrowDown className="w-3 h-3" /> Name (Z-A)
              </span>
            </SelectItem>
            <SelectItem value="provider-asc">
              <span className="flex items-center gap-2">
                <ArrowUp className="w-3 h-3" /> Provider (A-Z)
              </span>
            </SelectItem>
            <SelectItem value="provider-desc">
              <span className="flex items-center gap-2">
                <ArrowDown className="w-3 h-3" /> Provider (Z-A)
              </span>
            </SelectItem>
            <SelectItem value="status">
              <span className="flex items-center gap-2">
                <Shield className="w-3 h-3" /> By Verification
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : platforms.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Database className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No platforms in database yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Click &quot;Migrate&quot; above to import platform data.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* TechEx Platforms */}
          {techExPlatforms.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                TechEx Global 2026 ({techExPlatforms.length})
              </h3>
              <div className="grid gap-2">
                {techExPlatforms.map(platform => (
                  <Card 
                    key={platform.id} 
                    className={cn(
                      'transition-opacity',
                      !platform.is_enabled && 'opacity-50'
                    )}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {!platform.is_enabled && (
                            <EyeOff className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{platform.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{platform.provider}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="hidden sm:inline">{getVerificationBadge(platform)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(platform)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(platform)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Other Platforms */}
          {otherPlatforms.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Other Platforms ({otherPlatforms.length})
              </h3>
              <div className="grid gap-2">
                {otherPlatforms.map(platform => (
                  <Card 
                    key={platform.id}
                    className={cn(
                      'transition-opacity',
                      !platform.is_enabled && 'opacity-50'
                    )}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {!platform.is_enabled && (
                            <EyeOff className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{platform.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{platform.provider}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="hidden sm:inline">{getVerificationBadge(platform)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(platform)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(platform)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={!!editingPlatform} onOpenChange={() => setEditingPlatform(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isAddingNew ? 'Add New Platform' : `Edit: ${editingPlatform?.name}`}
            </DialogTitle>
            <DialogDescription>
              {isAddingNew 
                ? 'Enter the details for the new platform' 
                : 'Update platform information and admin settings'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Basic Information</h4>
              
              {isAddingNew && (
                <div className="space-y-2">
                  <Label>Platform ID *</Label>
                  <Input
                    value={editForm.id || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') 
                    }))}
                    placeholder="unique-platform-id"
                  />
                  <p className="text-xs text-muted-foreground">
                    Lowercase letters, numbers, and hyphens only
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Platform Name *</Label>
                  <Input
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Platform Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Input
                    value={editForm.provider || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, provider: e.target.value }))}
                    placeholder="Company Name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Summary</Label>
                <Textarea
                  value={editForm.summary || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief description of the platform..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Full Description</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detailed description..."
                  rows={4}
                />
              </div>
            </div>

            <Separator />

            {/* Admin Settings */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Admin Settings</h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Verification Status</Label>
                  <Select
                    value={editForm.verification_status || 'initial'}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, verification_status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationLevels.map(level => (
                        <SelectItem key={level.value} value={level.value}>
                          <span className="flex items-center gap-2">
                            <level.icon className={cn('w-4 h-4', level.className)} />
                            {level.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={editForm.is_enabled ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditForm(prev => ({ ...prev, is_enabled: true }))}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Visible
                    </Button>
                    <Button
                      type="button"
                      variant={!editForm.is_enabled ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditForm(prev => ({ ...prev, is_enabled: false }))}
                      className="flex-1"
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Hidden
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Source Tag</Label>
                <Input
                  value={editForm.sourceTag || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, sourceTag: e.target.value }))}
                  placeholder="e.g., TechEx Global 2026"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  placeholder="Internal notes about this platform..."
                  value={editForm.admin_notes || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            {/* Links */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                External Links
              </h4>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Website URL</Label>
                  <Input
                    value={editForm.links?.website || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      links: { ...prev.links, website: e.target.value }
                    }))}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Documentation URL</Label>
                  <Input
                    value={editForm.links?.documentation || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      links: { ...prev.links, documentation: e.target.value }
                    }))}
                    placeholder="https://docs.example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pricing URL</Label>
                  <Input
                    value={editForm.links?.pricing || ''}
                    onChange={(e) => setEditForm(prev => ({ 
                      ...prev, 
                      links: { ...prev.links, pricing: e.target.value }
                    }))}
                    placeholder="https://example.com/pricing"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditingPlatform(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isAddingNew ? 'Create Platform' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Platform?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
